import { theme } from 'antd';
import type { CSSProperties } from 'react';
import {
  AccountBookOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  GiftOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { formatCurrency } from '../../../lib/format';
import { useClienteResumen } from '../api/useClientes';
import { useT } from '../../../locales';
import { MetricCard } from '../../../components/MetricCard';

interface ClienteKPIsProps {
  clienteId: number;
}

// Las cards de ClienteKPIs conservan su padding horizontal original (22px);
// el resto de la familia MetricCard usa 18px.
const PADDING_KPI: CSSProperties = { padding: '18px 22px 20px' };

// Grid responsivo de 5 KPIs: auto-fit reparte el ancho en columnas iguales
// (5 en desktop, menos al angostar) sin la fila rota que dejaría un quinto
// card en un grid fijo de 4.
const GRID_KPI: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
  gap: 16,
  marginBottom: 16,
};

/**
 * Monetary headline for the 360 view. Errors degrade silently to '—': the KPIs
 * must never take the page down (the tab content below carries the detail).
 *
 * billing-domain-rules (Phase 8.3): deuda pendiente and saldo a favor are
 * surfaced as two independent, non-negative values sourced from
 * `ClienteResumenResponse.deudaPendiente` and `ClienteResumenResponse.saldoAFavor`
 * (view_client_balance). They MUST NOT be netted together (spec:
 * client-balance-reporting). The legacy `saldo`/`credito` aliases are
 * intentionally not displayed; consumers migrate to the two-value contract.
 */
export function ClienteKPIs({ clienteId }: ClienteKPIsProps) {
  const t = useT();
  const { token } = theme.useToken();
  const { data, isLoading, isError } = useClienteResumen(clienteId);

  const deudaValue = data?.deudaPendiente ?? '0';
  // Applicable credit (available ledger lots), NOT the statement net:
  // view_client_balance.saldo_a_favor also counts prepaid package reserves and
  // unbilled payments, so displaying it as credit advertises money the backend
  // cannot apply (invoice creation consumes cliente_credito lots FIFO).
  const saldoValue = data?.creditoDisponible ?? '0';
  const deudaNum = Number.parseFloat(deudaValue) || 0;
  const saldoNum = Number.parseFloat(saldoValue) || 0;

  return (
    <div style={GRID_KPI}>
      <MetricCard
        label={t('pages.clientes.kpi.totalFacturado')}
        value={isError ? '—' : formatCurrency(data?.totalFacturado ?? 0)}
        loading={isLoading}
        icon={<AccountBookOutlined />}
        caption={isError ? undefined : t('pages.clientes.kpi.totalFacturadoCaption')}
        style={PADDING_KPI}
      />
      <MetricCard
        label={t('pages.clientes.kpi.totalPagado')}
        value={isError ? '—' : formatCurrency(data?.totalPagado ?? 0)}
        loading={isLoading}
        icon={<CheckCircleOutlined />}
        caption={isError ? undefined : t('pages.clientes.kpi.totalPagadoCaption')}
        style={PADDING_KPI}
      />
      {/* billing-domain-rules (Phase 8.3): non-negative deuda pendiente
          sourced from view_client_balance.deuda_pendiente. Distinct from
          saldo a favor — never netted. */}
      <MetricCard
        label={t('pages.clientes.kpi.deudaPendiente')}
        value={
          isError ? (
            '—'
          ) : (
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                color: deudaNum > 0 ? token.colorError : undefined,
              }}
            >
              {formatCurrency(deudaValue)}
            </span>
          )
        }
        loading={isLoading}
        icon={<WarningOutlined />}
        caption={
          isError || deudaNum <= 0 ? undefined : t('pages.clientes.kpi.deudaPendienteCaption')
        }
        style={PADDING_KPI}
      />
      {/* billing-domain-rules (Phase 8.3): non-negative saldo a favor
          sourced from view_client_balance.saldo_a_favor. Distinct from
          deuda pendiente — never netted. */}
      <MetricCard
        label={t('pages.clientes.kpi.saldoAFavor')}
        value={
          isError || saldoNum <= 0 ? (
            '—'
          ) : (
            <span style={{ fontVariantNumeric: 'tabular-nums', color: token.colorSuccess }}>
              {formatCurrency(saldoValue)}
            </span>
          )
        }
        loading={isLoading}
        icon={<GiftOutlined />}
        caption={isError || saldoNum <= 0 ? undefined : t('pages.clientes.kpi.saldoAFavorCaption')}
        style={PADDING_KPI}
      />
      {/* Restored from commit 92e4894 (pre billing changes) with the monthly
          amount to charge as its caption. Count is the headline; the amount
          comes from the same /resumen aggregate that powers the count. */}
      <MetricCard
        label={t('pages.clientes.kpi.serviciosActivos')}
        value={isError ? '—' : String(data?.serviciosActivos ?? 0)}
        loading={isLoading}
        icon={<AppstoreOutlined />}
        caption={
          isError
            ? undefined
            : t('pages.clientes.kpi.serviciosActivosCaption', {
                monto: formatCurrency(data?.serviciosActivosMontoMensual ?? '0'),
              })
        }
        style={PADDING_KPI}
      />
    </div>
  );
}
